<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick"/>
    </section>
    <section class="dp-right student-dismissal-page">
      <toolbar :buttonList="buttonList" @callFunction="callFunction" :searchList="[]" />
      <el-card v-if="scopeId > 0" class="panel student-dismissal-card" shadow="never" :body-style="{ padding: '14px 16px', display: 'flex', flexDirection: 'column', minHeight: '0', flex: '1', overflow: 'hidden' }">
        <div slot="header" class="card-head card-header-flex">
          <div class="card-header-left">
            <span class="card-header-title">学生放学标签</span>
            <el-tag v-if="scopeLabel" size="small" type="primary" effect="plain" class="scope-tag">
              当前范围：{{ scopeLabel }}
            </el-tag>
          </div>
          <el-button
            v-if="campusId"
            type="text"
            size="mini"
            icon="el-icon-question"
            class="tips-toggle-btn"
            @click="tipsDialogVisible = true"
          >
            操作说明
          </el-button>
        </div>

        <el-alert
          v-if="scopeId > 0 && !campusId"
          class="scope-alert"
          type="warning"
          :closable="false"
          show-icon
          title="当前范围未解析到校区"
          description="请确认左侧选中的是学校/年级/班级节点，并在「放学标签配置」中为该校区维护标签项。"
        />
        <el-alert
          v-else-if="scopeId > 0 && campusId && !tagCategoryList.length"
          class="scope-alert"
          type="info"
          :closable="false"
          show-icon
          title="本校区尚未配置标签项"
          description="请前往「放学标签配置」新增标签项（如：中午用餐、下午托管），再回到本页为学生设置标签。"
        />


        <!-- 优化：利用 Flex 布局重构筛选面板，删除废弃的小标题并大幅收窄输入框宽度，使表单与操作按钮无缝在单行平铺呈现 -->
        <div class="filter-panel">
          <!-- 优化：移除 label-width，解决内联 flex 容器下 input 左侧被强制附加 margin-left 导致巨大间距的问题 -->
          <el-form size="small" class="filter-form-inline" @submit.native.prevent>
            <el-form-item label="卡号">
              <el-input
                v-model="filterCard"
                clearable
                placeholder="卡号模糊匹配"
                class="filter-input--card"
                @keyup.enter.native="() => loadStudentList(true)"
              />
            </el-form-item>
            <el-form-item label="姓名">
              <el-input
                v-model="filterName"
                clearable
                placeholder="姓名模糊匹配"
                class="filter-input--name"
                @keyup.enter.native="() => loadStudentList(true)"
              />
            </el-form-item>
            <el-form-item label="人员类型">
              <el-select v-model="filterLiveTypeId" clearable placeholder="全部" filterable class="filter-select filter-select--personnel">
                <el-option v-for="lt in effectiveLiveTypeOptions" :key="lt.LiveTypeId" :label="lt.LiveTypeName" :value="lt.LiveTypeId" />
              </el-select>
            </el-form-item>
            <el-form-item label="标签">
              <el-select v-model="filterTagId" clearable placeholder="全部" class="filter-select" :disabled="!campusId">
                <el-option :value="0" label="未设置" />
                <el-option
                  v-for="t in campusTagOptions"
                  :key="t.id"
                  :value="t.id"
                  :label="tagOptionLabel(t)"
                />
              </el-select>
            </el-form-item>
            <div class="filter-form-actions">
              <el-button type="primary" size="small" :loading="listLoading" :disabled="!campusId" @click="loadStudentList(true)">查询</el-button>
              <el-button size="small" :disabled="listLoading" @click="resetFiltersAndLoad">重置</el-button>
              <el-button type="primary" plain size="small" icon="el-icon-download" :loading="exporting" :disabled="!campusId || listLoading" @click="doExport">导出</el-button>
              <el-button type="success" plain size="small" icon="el-icon-upload2" :disabled="!campusId || listLoading" @click="openImportDialog">导入</el-button>
            </div>
          </el-form>
        </div>

        <!-- 列表容器：tbl-scroll-host 限高并在内部滑动。最后更新：2026-09-07 -->
        <div class="table-wrap tbl-scroll-host">
          <el-table
            ref="studentTable"
            v-loading="listLoading"
            :data="studentRows"
            border
            stripe
            size="small"
            height="100%"
            style="width: 100%"
          >
            <el-table-column prop="className" label="班级" min-width="120" show-overflow-tooltip :resizable="false" />
            <el-table-column prop="seatNumber" label="座号" width="72" align="center" :resizable="false" />
            <el-table-column prop="studentName" label="姓名" min-width="90" show-overflow-tooltip :resizable="false" />
            <el-table-column prop="stuCode" label="学号" min-width="100" show-overflow-tooltip :resizable="false" />
            <el-table-column prop="cardNumber" label="卡号" min-width="110" show-overflow-tooltip :resizable="false" />
            <el-table-column label="人员类型" min-width="110" align="center" show-overflow-tooltip :resizable="false">
              <template slot-scope="scope">
                {{ scope.row.liveTypeName || formatLiveType(scope.row.liveTypeId) }}
              </template>
            </el-table-column>
            <el-table-column label="标签" min-width="220" :resizable="false">
              <template slot-scope="scope">
                <!-- 优化：平铺展示学生的多个放学标签，使配置情况一眼掌握 -->
                <div
                  v-if="sortedTagCategoryList.length"
                  class="tag-pill-container"
                  @click="openTagDialog(scope.row)"
                >
                  <div v-if="hasAnyTag(scope.row)" class="tag-pill-row">
                    <span
                      v-for="cat in sortedTagCategoryList"
                      v-if="scope.row.tagByCategory[cat.categoryName]"
                      :key="cat.categoryName"
                      class="student-tag-pill"
                    >
                      <span class="pill-label">{{ cat.categoryName }}</span>
                      <span class="pill-value">{{ getTagNameById(cat, scope.row.tagByCategory[cat.categoryName]) }}</span>
                    </span>
                  </div>
                  <span v-else class="tag-cell-empty-text">
                    <i class="el-icon-plus" />
                    点击设置标签
                  </span>
                </div>
                <span v-else class="table-tag-empty">
                  <i class="el-icon-warning-outline" />
                  请先在<span class="tip-link">「放学标签配置」</span>中维护标签项
                </span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="96" align="center" fixed="right" :resizable="false">
              <template slot-scope="scope">
                <el-button
                  type="primary"
                  size="mini"
                  :disabled="!campusId || !sortedTagCategoryList.length"
                  @click="openTagDialog(scope.row)"
                >
                  设置标签
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 底部分页：固定停靠在卡片底端。最后更新：2026-09-07 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            :total="total"
            @size-change="onPageSizeChange"
            @current-change="onPageChange"
          />
        </div>
      </el-card>
      
      <!-- 优化：暂未选择组织树范围时的空状态视觉引导 -->
      <div v-else class="empty-tip">
        <div class="empty-tip-card">
          <div class="empty-icon-wrapper">
            <i class="el-icon-folder-opened" />
          </div>
          <p class="empty-text">暂未选择查看范围</p>
          <p class="empty-subtext">请先在左侧树结构中选择学校、年级或班级节点</p>
        </div>
      </div>

      <!-- 操作说明弹窗：与学生标签配置页一致。最后更新：2026-10-29 -->
      <el-dialog
        title="操作说明"
        :visible.sync="tipsDialogVisible"
        width="560px"
        append-to-body
        custom-class="student-tag-tips-dialog"
      >
        <div class="tip-panel tip-panel--dialog">
          <div class="tip-row">
            <span class="tip-label tip-label--scope">范围</span>
            <span class="tip-text">由左侧机构树决定（学校/年级/班级）。每个标签项下只能选一个选项，选好后点「保存」。</span>
          </div>
          <div class="tip-row">
            <span class="tip-label tip-label--batch">批量</span>
            <div class="tip-body">
              <span class="tip-text">导出/导入 Excel：</span>
              <span class="tip-rule">{{ tagImportBaseRule }}</span>
              <template v-if="tagTipState === 'noCampus'">
                <span class="tip-hint">选择左侧范围并解析到校区后，将显示本校区格式示例。</span>
              </template>
              <template v-else-if="tagTipState === 'noTags'">
                <span class="tip-warn">
                  <i class="el-icon-warning-outline" />
                  请先在<span class="tip-link">「学生标签配置」</span>为本校区维护标签项
                </span>
              </template>
              <template v-else>
                <span class="tip-example-label">本校区示例</span>
                <span
                  v-for="(sample, idx) in tagImportSamples"
                  :key="'sample-' + idx"
                  class="tip-sample-pill"
                >{{ sample }}</span>
              </template>
            </div>
          </div>
        </div>
        <div slot="footer">
          <el-button type="primary" @click="tipsDialogVisible = false">知道了</el-button>
        </div>
      </el-dialog>

      <el-dialog
        :title="tagDialogTitle"
        :visible.sync="tagDialogVisible"
        width="540px"
        :close-on-click-modal="false"
        append-to-body
        @closed="onTagDialogClosed"
      >
        <div v-if="tagEditRow" class="tag-dialog-picks">
          <div
            v-for="cat in sortedTagCategoryList"
            :key="cat.categoryName"
            class="tag-pick-row"
          >
            <span class="tag-pick-label">{{ cat.categoryName }}</span>
            
            <!-- 优化：选项较少（<= 5 个）时，直接展示为平铺的可点击小药丸，提升用户设置效率 -->
            <div v-if="cat.values && cat.values.length <= 5" class="tag-pill-option-group">
              <span
                v-for="t in cat.values"
                :key="t.id"
                :class="['tag-pill-option', String(tagEditRow.tagByCategory[cat.categoryName]) === String(t.id) ? 'is-active' : '']"
                @click="togglePillSelection(tagEditRow, cat.categoryName, t.id)"
              >
                {{ t.tagName }}
              </span>
              <!-- 快捷清除所选项按钮 -->
              <span
                v-if="tagEditRow.tagByCategory[cat.categoryName]"
                class="tag-pill-option-clear"
                title="清除选择"
                @click="togglePillSelection(tagEditRow, cat.categoryName, null)"
              >
                <i class="el-icon-close" />
              </span>
            </div>

            <!-- 选项较多（> 5 个）时，降级显示下拉选择框 -->
            <el-select
              v-else
              :value="tagEditRow.tagByCategory[cat.categoryName]"
              size="small"
              clearable
              placeholder="未设置"
              style="width: 240px"
              @change="(v) => onStudentCategoryTagChange(tagEditRow, cat.categoryName, v)"
            >
              <el-option
                v-for="t in cat.values"
                :key="t.id"
                :value="t.id"
                :label="t.tagName"
              />
            </el-select>
          </div>
        </div>
        <div slot="footer">
          <el-button @click="tagDialogVisible = false">取消</el-button>
          <el-button
            type="primary"
            :loading="tagEditRow && savingStudentId === tagEditRow.studentId"
            :disabled="!tagEditRow"
            @click="saveTagDialog"
          >
            保存
          </el-button>
        </div>
      </el-dialog>

      <!-- 批量导入放学标签弹窗：对齐人员导入规范样式并支持浏览文件。最后更新：2026-09-07 -->
      <el-dialog
        :visible.sync="importDialogVisible"
        width="860px"
        custom-class="person-import-dialog"
        :close-on-click-modal="false"
        append-to-body
        @closed="onImportDialogClosed"
      >
        <div slot="title" class="pi-dialog-title">
          <span class="pi-dialog-title__icon"><i class="el-icon-collection-tag" /></span>
          <div>
            <strong>批量导入学生放学标签</strong>
            <p>{{ scopeLabel ? `当前范围：${scopeLabel}` : '请先在左侧树结构选择范围' }} · 下载模板填写后上传，核对无误后再确认写入</p>
          </div>
        </div>

        <div class="pi-body">
          <div class="pi-steps">
            <div class="pi-step">
              <span class="pi-step__no">1</span>
              <div><strong>下载模板</strong><em>按当前范围导出学生与标签列</em></div>
            </div>
            <i class="el-icon-arrow-right pi-step__arrow" />
            <div class="pi-step">
              <span class="pi-step__no">2</span>
              <div><strong>上传 Excel</strong><em>支持 .xlsx / .xls 格式</em></div>
            </div>
            <i class="el-icon-arrow-right pi-step__arrow" />
            <div class="pi-step">
              <span class="pi-step__no">3</span>
              <div><strong>核对导入</strong><em>预览后再确认写入</em></div>
            </div>
          </div>

          <button
            type="button"
            class="pi-template-card"
            :disabled="!campusId"
            @click="doExport"
          >
            <span class="pi-template-card__icon"><i class="el-icon-document" /></span>
            <span class="pi-template-card__meta">
              <strong>学生放学标签导入模板.xlsx</strong>
              <em>含填写说明、示范案例行与校区可用标签字典，下载后可直接对照填写</em>
            </span>
            <span class="pi-template-card__action">
              <i v-if="exporting" class="el-icon-loading" />
              <template v-else><i class="el-icon-download" />下载模板</template>
            </span>
          </button>

          <el-upload
            :key="'tag-upload-' + importUploadKey"
            ref="importUpload"
            class="pi-upload"
            drag
            action="#"
            :auto-upload="false"
            :limit="1"
            :show-file-list="false"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            :on-change="onImportFileChange"
            :on-remove="onImportFileRemove"
            :on-exceed="onImportExceed"
          >
            <div v-if="importFileRaw" class="pi-upload__file">
              <i class="el-icon-success pi-upload__ok" />
              <div>
                <strong>{{ importFileRaw.name }}</strong>
                <em>已选择文件，点击「解析并预览」进入核对</em>
              </div>
            </div>
            <div v-else class="pi-upload__empty">
              <i class="el-icon-upload2" />
              <strong>点击浏览或拖拽 Excel 到此处</strong>
              <em>仅支持标准 .xlsx / .xls 文件</em>
            </div>
          </el-upload>
        </div>

        <div slot="footer" class="pi-footer">
          <el-button @click="importDialogVisible = false">关闭</el-button>
          <el-button
            type="primary"
            icon="el-icon-view"
            :disabled="!importFileRaw"
            :loading="importParsing"
            @click="parseImportFile"
          >
            解析并预览
          </el-button>
        </div>
      </el-dialog>

      <!-- 学生放学标签导入核对预览弹窗。最后更新：2026-09-07 -->
      <el-dialog
        :visible.sync="previewDialogVisible"
        width="78%"
        custom-class="person-import-dialog"
        :close-on-click-modal="false"
        append-to-body
        :before-close="(done) => { backToImportSelect(); done(); }"
      >
        <div slot="title" class="pi-dialog-title">
          <span class="pi-dialog-title__icon"><i class="el-icon-document-checked" /></span>
          <div>
            <strong>学生放学标签导入核对</strong>
            <p>共 {{ importRows.length }} 条，确认无误后写入系统</p>
          </div>
        </div>

        <el-table
          :data="pagedImportRows"
          class="pi-preview-table"
          border
          stripe
          style="width: 100%"
          max-height="520"
        >
          <el-table-column label="班级" min-width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.className || (scope.row.classId ? `ID:${scope.row.classId}` : '-') }}</span>
            </template>
          </el-table-column>
          <el-table-column label="学号" width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.stuCode || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="姓名" width="110" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.studentName || (scope.row.studentId ? `ID:${scope.row.studentId}` : '-') }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="tagNames" label="放学标签" min-width="180" show-overflow-tooltip>
            <template slot-scope="scope">
              <template v-if="scope.row.tagNames && scope.row.tagNames !== '未设置'">
                <el-tag
                  v-for="(tagItem, tIdx) in scope.row.tagNames.split(/[、,，;；]/).filter(Boolean)"
                  :key="tIdx"
                  size="mini"
                  style="margin-right: 4px;"
                >
                  {{ tagItem.trim() }}
                </el-tag>
              </template>
              <span v-else style="color: #909399;">未设置</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center" width="90" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" class="pi-row-del" @click="removeImportRow(scope)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="importRows.length > 10" class="pi-preview-pager">
          <el-pagination
            layout="total, sizes, prev, pager, next"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="previewPageSize"
            :current-page="previewPage"
            :total="importRows.length"
            @size-change="handlePreviewSizeChange"
            @current-change="handlePreviewCurrentChange"
          />
        </div>

        <div slot="footer" class="pi-footer">
          <el-button @click="backToImportSelect">返回上一步</el-button>
          <el-button
            type="primary"
            icon="el-icon-upload2"
            :disabled="!importRows.length"
            :loading="importing"
            @click="submitImport"
          >
            确认导入
          </el-button>
        </div>
      </el-dialog>

      <!-- 批量导入结果展示弹窗（与人员导入保持统一风格）。最后更新：2026-09-07 -->
      <import-result-dialog
        :visible.sync="importResultDialogVisible"
        :result="importResult"
      />
    </section>
  </section>
</template>

<script>
import "@/style/person-import-dialog.css";
import { read, utils, write } from "xlsx";
import {
  exportStudentDismissalTags,
  getDismissalTagCategories,
  getDismissalTags,
  getStudentDismissalTagQuery,
  importStudentDismissalTags,
  saveStudentDismissalTag
} from "@/api/dismissal";
import { getDepartmentListPage, getStudentLiveTypeList } from '../../api/modules/department';
import Toolbar from "../../components/Toolbar";
import DepartmentPane from "../../components/DepartmentPane";
import ImportResultDialog from "../../components/ImportResultDialog";
import { getButtonList } from "../../promissionRouter";
import { toDeptId, treeNodeDeptId, normalizeDeptId } from '../../utils/deptCascaderProps';

export default {
  name: "StudentDismissalCategory",
  components: { Toolbar, DepartmentPane, ImportResultDialog },
  data() {
    return {
      buttonList: [],
      scopeDeptOrgType: "",
      scopeId: 0,
      campusId: 0,
      schoolDepartmentId: 0,
      scopeLabel: "",
      tagCategoryList: [],
      campusTagOptions: [],
      filterCard: "",
      filterName: "",
      filterLiveTypeId: "",
      filterTagId: null,
      liveTypeOptions: [],
      studentRows: [],
      page: 1,
      pageSize: 10,
      total: 0,
      listLoading: false,
      savingStudentId: null,
      exporting: false,
      importing: false,
      importResultDialogVisible: false,
      importResult: null,
      importDialogVisible: false,
      previewDialogVisible: false,
      importFileRaw: null,
      importUploadKey: 0,
      importParsing: false,
      importRows: [],
      previewPage: 1,
      previewPageSize: 10,
      importKeepFileOnClose: false,
      tagDialogVisible: false,
      tagEditRow: null,
      tipsDialogVisible: false,
      nodeClickRequestId: 0 // 防竞态异步请求版本号
    };
  },
  mounted() {
    this.exporting = false;
    const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : [];
    this.buttonList = getButtonList(this.$route.path, routers);
    this._resizeHandler = () => {
      if (this.$refs.studentTable) {
        this.$refs.studentTable.doLayout();
      }
    };
    window.addEventListener("resize", this._resizeHandler);
  },
  beforeDestroy() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
  },
  computed: {
    tagImportBaseRule() {
      return "「标签名称」列按「标签项：选项值」填写；同一标签项只能填一个值；多项用顿号、逗号或分号分隔；留空表示清除。";
    },
    tagTipState() {
      if (!this.campusId) return "noCampus";
      if (!this.tagCategoryList || !this.tagCategoryList.length) return "noTags";
      return "ready";
    },
    tagImportSamples() {
      if (this.tagTipState !== "ready") return [];
      return this.sortedTagCategoryList.map((cat) => {
        const val = cat.values && cat.values.length ? cat.values[0].tagName : "选项";
        return `${cat.categoryName}：${val}`;
      });
    },
    /** 按配置页序号排序的标签项 */
    sortedTagCategoryList() {
      return [...(this.tagCategoryList || [])].sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
      );
    },
    tagDialogTitle() {
      if (!this.tagEditRow) return "设置放学标签";
      const name = this.tagEditRow.studentName || "";
      return name ? `设置放学标签 - ${name}` : "设置放学标签";
    },
    effectiveLiveTypeOptions() {
      return (this.liveTypeOptions || []).map((lt) => ({
        LiveTypeId: lt.LiveTypeId || lt.liveTypeId,
        LiveTypeName: lt.LiveTypeName || lt.liveTypeName || lt.LiveTypeId || lt.liveTypeId
      }));
    },
    liveTypeMap() {
      const map = {};
      (this.liveTypeOptions || []).forEach((lt) => {
        const id = lt.LiveTypeId || lt.liveTypeId;
        if (id != null) {
          map[id] = lt.LiveTypeName || lt.liveTypeName || id;
        }
      });
      return map;
    },

    /** 导入核对表格当前页切片数据。最后更新：2026-09-07 */
    pagedImportRows() {
      const list = this.importRows || [];
      const start = (this.previewPage - 1) * this.previewPageSize;
      return list.slice(start, start + this.previewPageSize);
    }
  },
  methods: {
    /**
     * 将人员类型标识编码统一转换为中文描述。创建于 2026-09-07
     * @param {string|number} liveTypeId 人员类型标识/编码
     * @returns {string}
     */
    formatLiveType(liveTypeId) {
      if (!liveTypeId) return "—";
      const idStr = String(liveTypeId).trim();
      if (this.liveTypeMap && this.liveTypeMap[idStr]) {
        return this.liveTypeMap[idStr];
      }
      const lower = idStr.toLowerCase();
      if (this.liveTypeMap && this.liveTypeMap[lower]) {
        return this.liveTypeMap[lower];
      }
      return idStr;
    },

    callFunction() {},

    tagOptionLabel(t) {
      const label = t.displayName || (t.categoryName ? `${t.categoryName}：${t.tagName}` : t.tagName);
      if (!t.applyToDismissal) return `${label}（仅档案）`;
      return label;
    },

    hasAnyTag(row) {
      if (!row || !row.tagByCategory) return false;
      return this.sortedTagCategoryList.some((cat) => row.tagByCategory[cat.categoryName] > 0);
    },

    getTagNameById(cat, tagId) {
      if (!cat || !tagId) return "";
      const t = (cat.values || []).find((x) => String(x.id) === String(tagId));
      return t ? t.tagName : "";
    },

    togglePillSelection(row, categoryName, tagId) {
      if (String(row.tagByCategory[categoryName]) === String(tagId)) {
        // 如果已选则清除该选项配置
        this.onStudentCategoryTagChange(row, categoryName, null);
      } else {
        this.onStudentCategoryTagChange(row, categoryName, tagId);
      }
    },

    openTagDialog(row) {
      if (!this.campusId) {
        this.$message.warning("当前范围未解析到校区");
        return;
      }
      if (!this.sortedTagCategoryList.length) {
        this.$message.warning("请先在「放学标签配置」中维护标签项");
        return;
      }
      const tagByCategory = {};
      if (row.tagByCategory) {
        Object.keys(row.tagByCategory).forEach((k) => {
          if (row.tagByCategory[k]) tagByCategory[k] = row.tagByCategory[k];
        });
      }
      this.tagEditRow = {
        ...row,
        tagByCategory
      };
      this.tagDialogVisible = true;
    },

    onTagDialogClosed() {
      this.tagEditRow = null;
    },

    saveTagDialog() {
      if (!this.tagEditRow) return;
      this.saveRow(this.tagEditRow).then((ok) => {
        if (ok) this.tagDialogVisible = false;
      });
    },

    onStudentCategoryTagChange(row, categoryName, tagId) {
      if (!row.tagByCategory) this.$set(row, "tagByCategory", {});
      if (tagId) {
        this.$set(row.tagByCategory, categoryName, String(tagId));
      } else {
        this.$delete(row.tagByCategory, categoryName);
      }
      row.tagIds = Object.values(row.tagByCategory).filter((id) => id);
    },

    buildTagByCategoryFromRow(r) {
      const map = r.tagIdByCategory || r.TagIdByCategory;
      if (map && typeof map === "object") {
        const out = {};
        Object.keys(map).forEach((k) => {
          if (map[k] != null && map[k] !== "") out[k] = String(map[k]);
        });
        return out;
      }
      const byCat = {};
      (r.tagIds || r.TagIds || []).forEach((id) => {
        const t = this.campusTagOptions.find((x) => String(x.id) === String(id));
        if (t && t.categoryName) byCat[t.categoryName] = String(id);
      });
      return byCat;
    },

    /** 从树节点向上找校区 Id（字符串）。最后修改：2026-10-18 */
    resolveCampusIdFromTreeNode(node) {
      let n = node;
      while (n && n.data) {
        const t = String((n.data.DeptOrgType != null ? n.data.DeptOrgType : n.data.deptOrgType) || "").toLowerCase();
        if (t === "campus") return treeNodeDeptId(n.data);
        n = n.parent;
      }
      return '';
    },

    async resolveCampusIdForDepartment(deptId) {
      let id = toDeptId(deptId);
      for (let i = 0; i < 12 && id; i++) {
        const res = await getDepartmentListPage({ id });
        if (!res.data.success || !res.data.response) return '';
        const dept = res.data.response;
        const t = String(dept.DeptOrgType != null ? dept.DeptOrgType : dept.deptOrgType || "").toLowerCase();
        if (t === "campus") return toDeptId(dept.Id);
        const pid = dept.Pid != null ? dept.Pid : dept.pid;
        id = toDeptId(pid);
        if (!id) return '';
      }
      return '';
    },

    async resolveCampusForScope(scopeDeptOrgType, scopeId, node) {
      if (scopeDeptOrgType === "campus") return toDeptId(scopeId);
      const fromTree = node ? this.resolveCampusIdFromTreeNode(node) : '';
      if (fromTree) return fromTree;
      const fromDept = await this.resolveCampusIdForDepartment(scopeId);
      if (fromDept) return fromDept;
      return '';
    },

    resolveSchoolDepartmentIdFromTreeNode(node) {
      let n = node && node.parent;
      while (n && n.data) {
        const t = String((n.data.DeptOrgType != null ? n.data.DeptOrgType : n.data.deptOrgType) || "").toLowerCase();
        if (t === "school" || t === "campus" || t === "affiliateschool") {
          return treeNodeDeptId(n.data);
        }
        n = n.parent;
      }
      const gradeNode = node && node.parent;
      const schoolNode = gradeNode && gradeNode.parent;
      if (schoolNode && schoolNode.data) {
        const sid = treeNodeDeptId(schoolNode.data);
        if (sid) return sid;
      }
      return '';
    },

    fetchLiveTypeOptions() {
      if (!this.schoolDepartmentId) {
        this.liveTypeOptions = [];
        return;
      }
      getStudentLiveTypeList({ id: this.schoolDepartmentId }).then((res) => {
        if (res.data.success && res.data.response) {
          this.liveTypeOptions = res.data.response;
        } else {
          this.liveTypeOptions = [];
        }
      });
    },

    loadCampusTagOptions() {
      const campusId = this.campusId;
      if (!campusId) {
        this.tagCategoryList = [];
        this.campusTagOptions = [];
        return Promise.resolve();
      }
      return Promise.all([
        getDismissalTagCategories({ campusId, page: 1, intPageSize: 500 }),
        getDismissalTags({ campusId })
      ]).then(([catRes, tagRes]) => {
        if (catRes.data.success && catRes.data.response) {
          const catPm = catRes.data.response;
          const catRows = catPm.data || catPm.Data || [];
          this.tagCategoryList = catRows
            .map((g) => ({
              categoryName: g.CategoryName != null ? g.CategoryName : g.categoryName,
              sortOrder: g.SortOrder != null ? g.SortOrder : g.sortOrder,
              values: (g.Values || g.values || []).map((t) => ({
                id: t.Id != null ? t.Id : t.id,
                tagName: t.TagName != null ? t.TagName : t.tagName
              }))
            }))
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        } else {
          this.tagCategoryList = [];
        }
        if (tagRes.data.success && tagRes.data.response) {
          this.campusTagOptions = (tagRes.data.response || []).map((t) => ({
            id: t.Id != null ? t.Id : t.id,
            categoryName: t.CategoryName != null ? t.CategoryName : t.categoryName,
            tagName: t.TagName != null ? t.TagName : t.tagName,
            displayName: t.DisplayName != null ? t.DisplayName : t.displayName,
            applyToDismissal: t.ApplyToDismissal != null ? t.ApplyToDismissal : t.applyToDismissal
          }));
        } else {
          this.campusTagOptions = [];
        }
      });
    },

    treeNodeClick(data, node) {
      const clearSelection = () => {
        this.scopeDeptOrgType = "";
        this.scopeId = 0;
        this.campusId = 0;
        this.schoolDepartmentId = 0;
        this.scopeLabel = "";
        this.tagCategoryList = [];
        this.campusTagOptions = [];
        this.studentRows = [];
        this.page = 1;
        this.total = 0;
        this.liveTypeOptions = [];
      };
      if (!data) {
        clearSelection();
        return;
      }
      const scopeKey = treeNodeDeptId(data);
      if (!scopeKey) {
        clearSelection();
        return;
      }

      const deptType = String(data.DeptOrgType != null ? data.DeptOrgType : data.deptOrgType || "").toLowerCase();
      this.scopeLabel = data.label || data.name || "";
      this.scopeDeptOrgType = deptType || "school";
      this.scopeId = scopeKey;

      const schoolLike = ["school", "campus", "affiliateschool"];
      const schoolFromTree = this.resolveSchoolDepartmentIdFromTreeNode(node);
      if (deptType === "class") {
        this.schoolDepartmentId = schoolFromTree || scopeKey;
      } else if (deptType === "grade") {
        this.schoolDepartmentId = schoolFromTree || scopeKey;
      } else if (schoolLike.includes(deptType)) {
        this.schoolDepartmentId = scopeKey;
      } else {
        this.schoolDepartmentId = schoolFromTree || scopeKey;
      }

      this.studentRows = [];
      this.fetchLiveTypeOptions();

      // 防竞态控制：每次点击自增 requestId 并缓存本地，用于阻断失效的异步 Promise 逻辑
      this.nodeClickRequestId += 1;
      const currentRequestId = this.nodeClickRequestId;

      this.resolveCampusForScope(this.scopeDeptOrgType, this.scopeId, node).then((campusId) => {
        if (currentRequestId !== this.nodeClickRequestId) return Promise.reject("requestId_expired");
        this.campusId = campusId;
        return this.loadCampusTagOptions();
      }).then(() => {
        if (currentRequestId !== this.nodeClickRequestId) return;
        this.loadStudentList(true);
      }).catch((err) => {
        if (err !== "requestId_expired") {
          console.error(err);
        }
      });
    },

    resetFiltersAndLoad() {
      if (this.listLoading) return;
      this.filterCard = "";
      this.filterName = "";
      this.filterLiveTypeId = "";
      this.filterTagId = null;
      this.loadStudentList(true);
    },

    onPageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.loadStudentList(false);
    },

    onPageChange(p) {
      this.page = p;
      this.loadStudentList(false);
    },

    loadStudentList(resetPage = false) {
      if (!this.scopeId || !this.scopeDeptOrgType) return;
      if (!this.campusId) {
        this.studentRows = [];
        this.total = 0;
        return;
      }
      if (this.listLoading) return;
      if (resetPage) this.page = 1;
      this.listLoading = true;
      const params = {
        scopeDeptOrgType: this.scopeDeptOrgType,
        scopeId: this.scopeId,
        page: this.page,
        intPageSize: this.pageSize
      };
      const campusKey = toDeptId(this.campusId);
      if (campusKey) params.campusId = campusKey;
      if (this.filterCard && String(this.filterCard).trim()) params.cardNumber = String(this.filterCard).trim();
      if (this.filterName && String(this.filterName).trim()) params.studentName = String(this.filterName).trim();
      if (this.filterLiveTypeId !== "" && this.filterLiveTypeId != null) params.liveTypeId = this.filterLiveTypeId;
      if (this.filterTagId !== null && this.filterTagId !== undefined && this.filterTagId !== "") {
        params.tagId = this.filterTagId;
      }

      getStudentDismissalTagQuery(params)
        .then((res) => {
          if (res.data.success && res.data.response) {
            const pm = res.data.response;
            const raw = pm.data || [];
            this.total = pm.dataCount != null ? pm.dataCount : 0;
            if (pm.page != null) this.page = pm.page;
            if (pm.PageSize != null) this.pageSize = pm.PageSize;
            this.studentRows = raw.map((r) => {
              const tagByCategory = this.buildTagByCategoryFromRow(r);
              return {
                classId: r.classId != null ? r.classId : r.ClassId,
                className: r.className != null ? r.className : r.ClassName,
                studentId: r.studentId != null ? r.studentId : r.StudentId,
                studentName: r.studentName != null ? r.studentName : r.StudentName,
                stuCode: r.stuCode != null ? r.stuCode : r.StuCode,
                cardNumber: r.cardNumber != null ? r.cardNumber : r.CardNumber,
                liveTypeId: r.liveTypeId != null ? r.liveTypeId : r.LiveTypeId,
                seatNumber: r.seatNumber != null ? r.seatNumber : r.SeatNumber,
                tagByCategory,
                tagIds: Object.values(tagByCategory).filter((id) => id > 0)
              };
            });
          } else {
            this.studentRows = [];
            this.total = 0;
            this.$message.warning((res.data && res.data.msg) || "加载失败");
          }
        })
        .catch(() => {
          this.studentRows = [];
          this.total = 0;
          this.$message.error("加载学生列表失败");
        })
        .finally(() => {
          this.listLoading = false;
        });
    },

    saveRow(row) {
      const classId = row.classId;
      if (!classId || !this.campusId) return Promise.resolve(false);
      this.savingStudentId = row.studentId;
      return saveStudentDismissalTag({
        classId,
        studentId: row.studentId,
        tagIds: Object.values(row.tagByCategory || {}).filter((id) => id)
      })
        .then((res) => {
          if (res.data.success) {
            this.$message.success("已保存");
            const sid = row.studentId;
            const listRow = this.studentRows.find((r) => r.studentId === sid);
            if (listRow && row.tagByCategory) {
              this.$set(listRow, "tagByCategory", { ...row.tagByCategory });
              listRow.tagIds = Object.values(row.tagByCategory).filter((id) => id > 0);
            } else {
              this.loadStudentList(false);
            }
            return true;
          }
          this.$message.error((res.data && res.data.msg) || "保存失败");
          return false;
        })
        .catch(() => {
          this.$message.error("保存请求失败");
          return false;
        })
        .finally(() => {
          this.savingStudentId = null;
        });
    },

    buildExportImportScopeParams() {
      const campusId = toDeptId(this.campusId);
      const scopeId = toDeptId(this.scopeId);
      const params = { campusId };
      if (this.scopeDeptOrgType === "class") params.classId = scopeId;
      else if (this.scopeDeptOrgType === "grade") params.gradeId = scopeId;
      else params.schoolId = scopeId;
      return params;
    },

    /**
     * 导出/下载模板：对齐 Person.vue 的 exportExcel / downloadParentTemplate。最后更新：2026-11-02
     */
    async doExport() {
      if (!toDeptId(this.scopeId) || !toDeptId(this.campusId)) {
        this.$message.warning("请先选择范围并确保已解析校区");
        return;
      }
      this.exporting = true;
      try {
        const res = await exportStudentDismissalTags(this.buildExportImportScopeParams());
        if (!res || !res.data) {
          this.$message.error("导出数据为空");
          return;
        }
        const url = window.URL.createObjectURL(
          new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `学生放学标签_${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success("导出成功");
      } catch (err) {
        console.error("学生放学标签导出失败", err);
        this.$message.error("导出失败");
      } finally {
        this.exporting = false;
      }
    },

    /** 打开导入对话框。最后更新：2026-09-07 */
    openImportDialog() {
      if (!this.campusId) {
        this.$message.warning("请先在左侧选择具体范围节点并确保已解析校区");
        return;
      }
      this.exporting = false;
      this.resetImportDialog();
      this.importDialogVisible = true;
    },

    /** 重置导入弹窗选择状态。最后更新：2026-09-07 */
    resetImportDialog() {
      this.importFileRaw = null;
      this.importUploadKey += 1;
      this.importParsing = false;
    },

    /** 导入弹窗关闭回调。最后更新：2026-09-07 */
    onImportDialogClosed() {
      if (this.importKeepFileOnClose) {
        this.importKeepFileOnClose = false;
        return;
      }
      this.resetImportDialog();
    },

    /** 选中导入文件。最后更新：2026-09-07 */
    onImportFileChange(file) {
      this.importFileRaw = file && file.raw ? file.raw : null;
    },

    /** 移除已选导入文件。最后更新：2026-09-07 */
    onImportFileRemove() {
      this.importFileRaw = null;
    },

    /** 文件超出限制时自动替换为最新文件。最后更新：2026-09-07 */
    onImportExceed(files) {
      const nextFile = files && files[0];
      if (!nextFile) return;
      this.importUploadKey += 1;
      this.$nextTick(() => {
        this.importFileRaw = nextFile.raw || nextFile;
      });
    },

    /** 前端解析选中的 Excel 文件并打开核对预览弹窗。最后更新：2026-09-07 */
    parseImportFile() {
      if (!this.importFileRaw) {
        this.$message.warning("请先浏览选择要导入的 Excel 文件");
        return;
      }
      const file = this.importFileRaw;
      if (!/\.(xls|xlsx)$/i.test(String(file.name || ""))) {
        this.$message.error("上传格式不正确，请上传 .xlsx 或 .xls 格式文件");
        return;
      }

      this.importParsing = true;
      const loading = this.$loading({
        lock: true,
        text: "正在读取并解析 Excel 文件...",
        background: "rgba(0, 0, 0, 0.7)"
      });

      const fileReader = new FileReader();
      fileReader.onload = (ev) => {
        try {
          const data = ev.target.result;
          const workbook = read(data, { type: "binary" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const aoa = utils.sheet_to_json(worksheet, { header: 1 });
          loading.close();
          this.importParsing = false;

          if (!aoa || aoa.length < 2) {
            this.$message.warning("所选工作表中未读取到任何数据行");
            return;
          }

          // 动态寻找表头行，兼容第0行或第1行，并映射列索引
          let headerRowIdx = -1;
          const colMap = {};
          for (let r = 0; r < Math.min(15, aoa.length); r++) {
            const row = aoa[r] || [];
            const rowTexts = row.map((col) => String(col || "").trim().toLowerCase());

            // 必须排除说明与提示行（说明行内可能提及班级、学生、标签等词，但不是真正的表头）
            const isNoteRow = rowTexts.some((t) =>
              t.includes("填写说明") ||
              t.includes("注意事项") ||
              (t.includes("说明") && t.length > 15)
            );
            if (isNoteRow) continue;

            const hasClass = rowTexts.some((t) => t.includes("班级"));
            const hasStudent = rowTexts.some((t) => t.includes("学生") || t.includes("姓名") || t.includes("学号"));
            const hasTag = rowTexts.some((t) => t.includes("标签"));

            if (hasClass && (hasStudent || hasTag)) {
              headerRowIdx = r;
              row.forEach((col, idx) => {
                const text = String(col || "").trim().toLowerCase();
                if (text.includes("班级id") || text === "classid") colMap.classId = idx;
                else if (text.includes("班级") || text === "classname" || text === "deptname") colMap.className = idx;
                else if (text.includes("学生id") || text === "studentid" || text === "userid") colMap.studentId = idx;
                else if (text.includes("学号") || text === "stucode") colMap.stuCode = idx;
                else if (text.includes("姓名") || text === "studentname" || text === "name") colMap.studentName = idx;
                else if (text.includes("标签") || text.includes("tag")) colMap.tagNames = idx;
              });
              break;
            }
          }

          const startRow = headerRowIdx >= 0 ? headerRowIdx + 1 : 1;

          const getVal = (row, key, defaultIdx) => {
            const idx = (colMap[key] !== undefined && colMap[key] !== null) ? colMap[key] : defaultIdx;
            if (idx === undefined || idx === null || idx < 0 || idx >= row.length) return "";
            const v = row[idx];
            return v != null ? String(v).trim() : "";
          };

          const rows = [];
          let sampleCount = 0;
          for (let i = startRow; i < aoa.length; i++) {
            const row = aoa[i];
            if (!row || row.length === 0) continue;
            const classId = getVal(row, "classId", 0);
            const className = getVal(row, "className", 1);
            const studentId = getVal(row, "studentId", 2);
            const stuCode = getVal(row, "stuCode", 3);
            const studentName = getVal(row, "studentName", 4);
            const tagNames = getVal(row, "tagNames", 5);

            if (!classId && !className && !studentId && !studentName && !stuCode) continue;

            // 过滤说明行与提示行
            const combined = `${classId}${className}${studentId}${stuCode}${studentName}`;
            if (combined.includes("填写说明") || combined.includes("说明") || combined.includes("注意事项")) continue;

            // 过滤示例案例行
            const isSample = combined.includes("示例") || /sample/i.test(combined);
            if (isSample) {
              sampleCount++;
              continue;
            }

            rows.push({
              rowNumber: i + 1,
              classId,
              className,
              studentId,
              stuCode,
              studentName,
              tagNames: tagNames || "未设置"
            });
          }

          if (rows.length === 0) {
            if (sampleCount > 0) {
              this.$message.warning("Excel 中仅检测到模板示例数据，请填入真实的班级学生数据后再核对导入");
            } else {
              this.$message.warning("Excel 中未检测到有效的学生放学标签记录");
            }
            return;
          }

          if (sampleCount > 0) {
            this.$message.info(`成功读取 ${rows.length} 条待核对数据（已自动过滤 ${sampleCount} 条模板示例行）`);
          }

          this.importRows = rows;
          this.previewPage = 1;
          this.importKeepFileOnClose = true;
          this.importDialogVisible = false;
          this.$nextTick(() => {
            this.previewDialogVisible = true;
          });
        } catch (e) {
          loading.close();
          this.importParsing = false;
          this.$message.error("Excel 文件解析失败，请确认文件完整有效");
        }
      };

      fileReader.onerror = () => {
        loading.close();
        this.importParsing = false;
        this.$message.error("文件读取失败");
      };

      fileReader.readAsBinaryString(file);
    },

    /** 从核对弹窗返回上一步选择文件。最后更新：2026-09-07 */
    backToImportSelect() {
      this.previewDialogVisible = false;
      this.importDialogVisible = true;
    },

    /** 删除单条待核对记录。最后更新：2026-09-07 */
    removeImportRow(scope) {
      const idx = (this.previewPage - 1) * this.previewPageSize + scope.$index;
      const studentName = (scope.row && scope.row.studentName) || "该记录";
      this.$confirm(`确定要从本次导入中移除学生「${studentName}」吗？`, "提示", {
        type: "warning"
      })
        .then(() => {
          this.importRows.splice(idx, 1);
          if (this.pagedImportRows.length === 0 && this.previewPage > 1) {
            this.previewPage -= 1;
          }
          this.$message.success("已移除");
        })
        .catch(() => {});
    },

    /** 切换核对分页大小。最后更新：2026-09-07 */
    handlePreviewSizeChange(val) {
      this.previewPageSize = val;
      this.previewPage = 1;
    },

    /** 切换核对当前页。最后更新：2026-09-07 */
    handlePreviewCurrentChange(val) {
      this.previewPage = val;
    },

    /** 将核对后的数据生成 Excel 提交给服务端入库。最后更新：2026-09-07 */
    submitImport() {
      if (!this.importRows || !this.importRows.length) {
        this.$message.warning("没有可导入的数据");
        return;
      }
      if (!this.campusId) {
        this.$message.warning("当前未解析到校区，无法执行导入");
        return;
      }

      this.importing = true;
      try {
        const headers = ["班级ID", "班级名称", "学生ID", "学号", "姓名", "标签名称"];
        const aoa = [headers];
        this.importRows.forEach((r) => {
          aoa.push([
            r.classId || "",
            r.className || "",
            r.studentId || "",
            r.stuCode || "",
            r.studentName || "",
            r.tagNames === "未设置" ? "" : r.tagNames
          ]);
        });

        const worksheet = utils.aoa_to_sheet(aoa);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, "学生放学标签");
        const excelBuffer = write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const fileName = (this.importFileRaw && this.importFileRaw.name) || "学生放学标签导入核对.xlsx";
        const file = new File([blob], fileName, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const form = new FormData();
        form.append("file", file);

        importStudentDismissalTags(form, this.campusId)
          .then((res) => {
            const d = res.data;
            if (d && d.success && d.response) {
              const r = d.response;
              const successCount = r.successCount != null ? r.successCount : (r.SuccessCount != null ? r.SuccessCount : 0);
              const errs = r.errors || r.Errors || [];
              const failCount = errs.length;

              // 构建与人员导入标准弹窗一致的结果对象
              this.importResult = {
                totalCount: successCount + failCount,
                successCount: successCount,
                failCount: failCount,
                summary: d.msg || `成功 ${successCount} 条，失败 ${failCount} 条`,
                failures: errs.map((x) => ({
                  rowIndex: x.rowNumber != null ? x.rowNumber : (x.RowNumber != null ? x.RowNumber : 0),
                  message: x.message || x.Message || "",
                  category: "放学标签"
                }))
              };

              this.previewDialogVisible = false;
              this.importResultDialogVisible = true;
              this.loadStudentList(true);
            } else {
              this.$message.error((d && d.msg) || "导入失败");
            }
          })
          .catch(() => {
            this.$message.error("导入请求失败");
          })
          .finally(() => {
            this.importing = false;
          });
      } catch (err) {
        this.importing = false;
        this.$message.error("构建导入数据失败");
      }
    },

    /** 兼容旧的上传文件变更调用。最后更新：2026-09-07 */
    onFileChange(file) {
      this.onImportFileChange(file);
      if (this.importFileRaw) {
        this.parseImportFile();
      }
    }
  }
};
</script>

<style scoped>
.student-dismissal-page {
  height: 100%;
  max-height: 100%;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}

.panel.student-dismissal-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.student-dismissal-card >>> .el-card__header {
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.student-dismissal-card >>> .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 14px 16px;
}

.card-header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.card-header-title {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
}
.scope-tag {
  border-radius: 4px;
  font-weight: 500;
}
.tips-toggle-btn {
  padding: 0;
  font-size: 13px;
  color: #909399;
  transition: color 0.2s ease;
}
.tips-toggle-btn:hover {
  color: var(--btn-primary, #2563eb);
}
.tip-panel {
  background: linear-gradient(180deg, #f8fafc 0%, #f5f7fa 100%);
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.65;
}
.tip-panel--dialog {
  margin: 0;
}
.tip-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.tip-row + .tip-row {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e4e7ed;
}
.tip-label {
  flex-shrink: 0;
  min-width: 40px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  border-radius: 4px;
}
.tip-label--scope {
  color: var(--btn-primary, #2563eb);
  background: #ecf5ff;
  border: 1px solid #c6e2ff;
}
.tip-label--batch {
  color: #67c23a;
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
}
.tip-body {
  flex: 1;
  min-width: 0;
}
.tip-text {
  color: #606266;
}
.tip-rule {
  display: block;
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}
.tip-hint {
  display: block;
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}
.tip-warn {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
  padding: 4px 10px;
  color: #e6a23c;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 4px;
  font-size: 12px;
}
.tip-warn .el-icon-warning-outline {
  font-size: 14px;
}
.tip-example-label {
  display: inline-block;
  margin-top: 6px;
  margin-right: 6px;
  color: #303133;
  font-weight: 600;
  font-size: 12px;
}
.tip-sample-pill {
  display: inline-flex;
  align-items: center;
  margin: 4px 6px 0 0;
  padding: 3px 10px;
  font-size: 12px;
  color: #1890ff;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 14px;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(24, 144, 255, 0.05);
}
.tip-link {
  color: var(--btn-primary, #2563eb);
  font-weight: 600;
}
.scope-alert {
  flex-shrink: 0;
  margin-bottom: 12px;
}
.scope-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 12px;
}
.scope-line-label {
  padding: 2px 8px;
  color: #606266;
  background: #f4f4f5;
  border-radius: 4px;
  font-size: 12px;
}
.scope-line-value {
  color: var(--btn-primary, #2563eb);
  font-weight: 600;
}
.table-tag-empty {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: #e6a23c;
  background: #fdf6ec;
  border-radius: 4px;
}
.table-tag-empty .el-icon-warning-outline {
  font-size: 14px;
}
.filter-panel {
  flex-shrink: 0;
  background: #f8fafc;
  border: 1px solid #f0f2f5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}
.filter-form-inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px; /* 间距控制 */
}
.filter-form-inline >>> .el-form-item {
  margin: 0 !important; /* 剔除原来造成重叠的 margin */
  display: inline-flex;
  align-items: center;
}
.filter-form-inline >>> .el-form-item__label {
  color: #606266;
  padding-right: 8px;
  font-weight: 500;
}
.filter-form-inline >>> .el-form-item__content {
  line-height: 1;
  margin-left: 0 !important; /* 强制消除可能继承或遗留的左外边距，收窄控件与标签的间距 */
}
.filter-input--card {
  width: 140px;
}
.filter-input--name {
  width: 120px;
}
.filter-select {
  width: 120px;
  max-width: 100%;
}
.filter-select.filter-select--personnel {
  width: 130px;
}
.filter-form-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto; /* 自动靠最右，使长表单两端平衡 */
}
.filter-form-actions .el-button {
  min-width: 64px;
  text-align: center;
  transition: all 0.2s ease;
}
.tag-cell-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  cursor: pointer;
  padding: 2px 0;
}
.tag-cell-summary:hover .tag-cell-main {
  color: var(--btn-primary, #2563eb);
}
.tag-cell-main {
  color: #303133;
  font-size: 13px;
}
.tag-cell-more {
  font-size: 12px;
  color: #909399;
  background: #f4f4f5;
  padding: 0 6px;
  border-radius: 10px;
}
.tag-dialog-picks {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
}
.tag-pick-row {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.tag-pick-row:hover {
  background: #f5f7fa;
  border-color: #e4e7ed;
}
.tag-pick-label {
  flex-shrink: 0;
  min-width: 90px;
  text-align: right;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}
.table-wrap.tbl-scroll-host {
  flex: 1;
  min-height: 0;
  height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-top: 0;
}
/* 与机构管理页一致：不写 height !important，表体用 flex-basis:auto。最后修改：2026-10-14 */
.table-wrap.tbl-scroll-host >>> .el-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.table-wrap.tbl-scroll-host >>> .el-table .el-table__header-wrapper {
  flex-shrink: 0;
  overflow: hidden;
}
.table-wrap.tbl-scroll-host >>> .el-table .el-table__body-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: auto !important;
  overflow-y: auto !important;
}
.pagination-container {
  flex-shrink: 0;
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}
.empty-tip {
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 24px;
  height: 100%;
}
.empty-tip-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
  text-align: center;
}
.empty-icon-wrapper {
  font-size: 48px;
  color: #c0c4cc;
  background: #f5f7fa;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
}
.empty-text {
  font-size: 15px;
  color: #606266;
  font-weight: 500;
  margin: 0 0 8px 0;
}
.empty-subtext {
  font-size: 13px;
  color: #909399;
  margin: 0;
  line-height: 1.4;
}

/* 新增的平铺药丸样式 */
.tag-pill-container {
  display: inline-block;
  cursor: pointer;
  width: 100%;
  min-height: 24px;
}
.tag-pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.student-tag-pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  border-radius: 12px; /* 升级为大圆角拼接胶囊 */
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  color: #1890ff;
  overflow: hidden;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(24, 144, 255, 0.04);
}
.student-tag-pill .pill-label {
  background: #bae7ff;
  color: #0050b3;
  padding: 2px 8px 2px 10px; /* 优化间距 */
  font-weight: 500;
}
.student-tag-pill .pill-value {
  padding: 2px 10px 2px 8px; /* 优化间距 */
  font-weight: bold;
}
.tag-cell-empty-text {
  font-size: 12px;
  color: #909399;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  margin: 2px 0; /* 缓冲外边距：防止贴底贴顶，解决 hover 状态下顶部边框被单元格 overflow 剪裁的问题 */
  border: 1px dashed #dcdfe6; /* 升级为虚线边框 */
  border-radius: 12px;
  background: #fafafa;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.tag-pill-container:hover .tag-cell-empty-text {
  color: var(--btn-primary, #2563eb);
  border-color: var(--btn-primary, #2563eb);
  border-style: solid; /* 悬浮变为实线 */
  background: #e6f7ff;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.12);
  position: relative; /* 提升渲染层级，防止被表格横线覆盖 */
  z-index: 2;
}

/* 新增的弹窗分段控制器 (Segmented Control) 选项样式 */
.tag-pill-option-group {
  display: inline-flex;
  background: #f0f2f5; /* 浅灰色背景底座 */
  padding: 4px;
  border-radius: 20px; /* 圆角底座 */
  align-items: center;
  gap: 2px;
}
.tag-pill-option {
  padding: 4px 16px;
  font-size: 13px;
  border: none; /* 剔除多余边框 */
  border-radius: 16px;
  color: #606266;
  background: transparent;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tag-pill-option:hover {
  color: var(--btn-primary, #2563eb);
  background: rgba(64, 158, 255, 0.08);
}
.tag-pill-option.is-active {
  color: #fff;
  background: var(--btn-primary, #2563eb);
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.35); /* 选中时的投影效果 */
}
.tag-pill-option-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: transparent;
  color: #909399;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 4px;
}
.tag-pill-option-clear:hover {
  background: #ff4d4f;
  color: #fff;
}
</style>
